import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import qs from 'qs';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { useModel } from 'hooks/useModel';
import { useStore } from 'models/Provider';

import { SlackSettings } from './Models';

export const SlackSettingsPage = observer(() => {
  const { api, t } = useStore();
  const { showSuccess, showError } = useToaster();
  const location = useLocation();
  const model = useModel(SlackSettings, {}, { api, showError, showSuccess, t });
  const authUrl = 'https://slack.com/oauth/v2/authorize';
  const helpUrl = 'https://help.50skills.com/en/articles/2708761-how-do-i-enable-an-integration-with-slack'; // prettier-ignore
  const scopes = model.scopes;
  const userScopes = model.userScopes;
  const clientId = '43278704066.262284113062';
  const redirectUri = `${window.location.origin}/settings/apps/slack/`;
  const url = `${authUrl}?scope=${scopes.join()}&user_scope=${userScopes.join()}&client_id=${clientId}&redirect_uri=${redirectUri}`;

  useEffect(() => {
    const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
    const code = parsed.code;
    if (code) {
      api.integrations.authenticateSlack(code, redirectUri).then((res) => {
        if (res && res.status === 200) {
          if (res.data && res.data.bot) {
            showSuccess(t('slack.success.bot'));
          } else {
            showSuccess(t('slack.success.user'));
          }
          model.getIsConnected();
        }
      });
    }
  }, [showSuccess, showError, t, api.users, model, api.integrations, redirectUri, location.search]);

  return (
    <Stack space={2}>
      <Text font="heading2" text={t('company.settings.apps.slack')} />
      {model.loaded && (
        <Stack space={6}>
          <div>
            {model.connected && (
              <Badge variant="green" text={t('integrations.slack.settings.header.connected')} />
            )}
            {!model.connected && (
              <Badge
                variant="neutral"
                text={t('integrations.slack.settings.header.notconnected')}
              />
            )}
          </div>
          <Separator />
          <Stack space={4}>
            <Text text={t('integrations.slack.settings.info')} />
            <Text text={t('integrations.slack.settings.info.a')} />
            <Text text={t('integrations.slack.settings.info.b')} />
            <Text text={t('integrations.slack.settings.info.c')} />
            <Text text={t('integrations.slack.settings.info.d')} />
          </Stack>
          <Stack orientation="horizontal" space={2} justify="flex-end">
            <Button
              variant="secondary"
              as="a"
              target="_blank"
              href={helpUrl}
              text={t('integrations.slack.settings.buttons.learnmore')}
            />
            <Button
              as="a"
              href={url}
              text={
                model.connected
                  ? t('integrations.slack.settings.buttons.reconnect')
                  : t('integrations.slack.settings.buttons.connect')
              }
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
});
